import React, { useState, useEffect } from 'react';
import './EliminationDinner.css';

function EliminationDinner() {
  const [numberCount, setNumberCount] = useState(0); // Number of cubes to render
  const [numbers, setNumbers] = useState([]); // Array of uneliminated numbers
  const [eliminatedNumbers, setEliminatedNumbers] = useState([]); // Array of eliminated numbers
  const [names, setNames] = useState({}); // Map of numbers to names
  const [isStarted, setIsStarted] = useState(false); // Track if the grid is displayed
  const [searchQuery, setSearchQuery] = useState(''); // Search input value
  const [searchResult, setSearchResult] = useState(''); // Search result message
  const [showIntro, setShowIntro] = useState(true);
  const [introFading, setIntroFading] = useState(false);

  const handleBegin = () => {
    if (numberCount > 0) {
      const generatedNumbers = Array.from({ length: numberCount }, (_, i) => i + 1); // Generate numbers 1 to numberCount
      setNumbers(generatedNumbers);
      setEliminatedNumbers([]);
      setNames({});
      setSearchQuery('');
      setSearchResult('');
      setIsStarted(true); // Switch to the grid display state
    } else {
      alert('Please select a valid number of cubes.');
    }
  };

  const handleEliminate = (number) => {
    setNumbers((prevNumbers) => prevNumbers.filter((n) => n !== number)); // Remove the number from the grid
    setEliminatedNumbers((prevEliminated) => [...prevEliminated, number]); // Add the number to the eliminated list
  };

  const handleUneliminate = (number) => {
    setEliminatedNumbers((prevEliminated) =>
      prevEliminated.filter((n) => n !== number)
    ); // Remove the number from the eliminated list
    setNumbers((prevNumbers) => [...prevNumbers, number].sort((a, b) => a - b)); // Add the number back to the grid and sort
  };

  const handleAddName = (number) => {
    const name = prompt('Enter a name for this number:'); // Prompt the user for a name
    if (name) {
      setNames((prevNames) => ({ ...prevNames, [number]: name })); // Add the name to the map
    }
  };

  const handleDeleteName = (number) => {
    setNames((prevNames) => {
      const updatedNames = { ...prevNames };
      delete updatedNames[number]; // Remove the name from the map
      return updatedNames;
    });
  };

  const handleSearch = () => {
    const query = searchQuery.trim().toLowerCase();
    let found = false;

    // Check if the query matches a number
    if (!isNaN(query)) {
      const number = parseInt(query, 10);
      if (eliminatedNumbers.includes(number)) {
        setSearchResult(`${number} is eliminated.`);
        found = true;
      } else if (numbers.includes(number)) {
        setSearchResult(`${number} is still active!`);
        found = true;
      }
    }

    // Check if the query matches a name
    Object.entries(names).forEach(([num, name]) => {
      if (name.toLowerCase() === query) {
        if (eliminatedNumbers.includes(parseInt(num, 10))) {
          setSearchResult(`${num}/${name} is eliminated.`);
        } else {
          setSearchResult(`${num}/${name} is still active!`);
        }
        found = true;
      }
    });

    if (!found) {
      setSearchResult('No result.');
    }
  };

  const resetGame = () => {
    setNumberCount(0);
    setNumbers([]);
    setEliminatedNumbers([]);
    setNames({});
    setSearchQuery('');
    setSearchResult('');
    setIsStarted(false);
  };

  useEffect(() => {
    // show overlay, start fade after 2s, hide after fade duration (2.5s)
    const fadeTimer = setTimeout(() => setIntroFading(true), 2000);
    const hideTimer = setTimeout(() => setShowIntro(false), 2000 + 2500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <>
      {showIntro && (
        <div
          className={`intro-overlay ${introFading ? 'fade-out' : ''}`}
          onClick={() => setShowIntro(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowIntro(false); }}
        >
          <h1 className="intro-h1">Elimination Dinner</h1>
        </div>
      )}

      <div className="elimination-dinner-container">
      {!isStarted ? (
        <div className="setup-container">
          <h1>Elimination Dinner</h1>
          <label>
            <strong>Select Number of Participants:</strong>
          </label>
          <input
            type="number"
            value={numberCount}
            onChange={(e) => setNumberCount(Number(e.target.value))}
            className="number-input"
            min="1"
          />
          <button onClick={handleBegin} className="begin-button">
            Begin
          </button>
        </div>
      ) : (
        <div className="grid-container">
          <h1>Elimination Grid</h1>
          <div className="grid">
            {numbers.map((number) => (
              <div key={number} className="grid-item">
                <span className="grid-number">{number}</span>
                {names[number] ? (
                  <>
                    <p className="name-display">{names[number]}</p>
                    <button
                      onClick={() => handleDeleteName(number)}
                      className="delete-name-button"
                    >
                      Delete Name
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleAddName(number)}
                    className="add-name-button"
                  >
                    Add Name
                  </button>
                )}
                <button
                  onClick={() => handleEliminate(number)}
                  className="eliminate-button"
                >
                  Eliminate
                </button>
              </div>
            ))}
          </div>
          <div className="eliminated-section">
            <h2>Eliminated Numbers</h2>
            <div className="eliminated-grid">
              {eliminatedNumbers.map((number) => (
                <div key={number} className="eliminated-item">
                  <span>{number}</span>
                  <button
                    onClick={() => handleUneliminate(number)}
                    className="uneliminate-button"
                  >
                    Uneliminate
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="search-section">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by number or name"
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
            {searchResult && <p className="search-result">{searchResult}</p>}
          </div>
          <button onClick={resetGame} className="reset-button">
            Reset
          </button>
        </div>
      )}
    </div>
    </>
  );
}

export default EliminationDinner;